// app/javascript/block-registry.ts
//
// Registers all @opensite/ui block components with @page-speed/blocks's
// renderer registry. Must be called once before the app renders.
//
// @opensite/ui block _type values correspond to the `id` field in @opensite/ui's
// block registry. Props are deserialized from the JSON __jsx format before
// being forwarded to the component.
import React from "react";
import { registerBlockRenderer } from "@page-speed/blocks";
import { getAllBlocks } from "@opensite/ui/registry";
import { DynamicIcon } from "@opensite/ui";
import { compileMarkdown } from "@page-speed/markdown-to-jsx";
import type { BlockRendererProps } from "@page-speed/blocks/types";

// Native HTML tags that can appear as __jsx values in serialized payloads.
const HTML_TAGS = new Set([
  "div", "span", "p", "a", "button", "section", "article", "aside",
  "header", "footer", "main", "nav", "ul", "ol", "li",
  "h1", "h2", "h3", "h4", "h5", "h6",
  "img", "picture", "figure", "figcaption", "br", "hr", "strong", "em",
]);

// Named React components that can appear as __jsx values.
const JSX_COMPONENTS: Record<string, React.ComponentType<any>> = {
  DynamicIcon,
};

// Recursively converts { __jsx, ...props } objects into React elements.
// Plain objects and arrays are walked so nested __jsx values are also resolved.
function deserializeJsx(value: unknown): unknown {
  if (value === null || value === undefined || typeof value !== "object") {
    return value;
  }

  if (Array.isArray(value)) {
    return value.map(deserializeJsx);
  }

  const obj = value as Record<string, unknown>;

  if ("__jsx" in obj) {
    const { __jsx: tag, children, ...rest } = obj;
    const type = tag as string;

    const resolvedProps: Record<string, unknown> = {};
    for (const [k, v] of Object.entries(rest)) {
      resolvedProps[k] = deserializeJsx(v);
    }

    let resolvedChildren: React.ReactNode[] | undefined;
    if (children !== undefined) {
      const c = deserializeJsx(children);
      resolvedChildren = (Array.isArray(c) ? c : [c]) as React.ReactNode[];
    }

    const component: string | React.ComponentType<any> =
      JSX_COMPONENTS[type] ?? (HTML_TAGS.has(type) ? type : "span");

    return resolvedChildren
      ? React.createElement(component, resolvedProps, ...resolvedChildren)
      : React.createElement(component, resolvedProps);
  }

  // Plain object — deserialize each value. Markup-looking STRING values are
  // compiled to real elements here (key-aware, see below); everything else
  // recurses.
  const result: Record<string, unknown> = {};
  for (const [k, v] of Object.entries(obj)) {
    result[k] = shouldHydrateMarkupString(k, v)
      ? compileMarkdown(v as string, HYDRATION_MARKDOWN_OPTIONS)
      : deserializeJsx(v);
  }
  return result;
}

// compileMarkdown's defaults do NOT drop <script>/<style> from embedded raw
// HTML (verified against 0.0.7) — they would materialize as real, executable
// elements. The content originates from our own generation pipeline, not end
// users, but a hydration path must not be able to inject executable script.
const HYDRATION_MARKDOWN_OPTIONS = {
  overrides: {
    script: () => null,
    style: () => null,
  },
};

// ReactNode-typed props (e.g. feature-showcase items[].content and
// .mediaComponent) arrive from the generation pipeline as HTML STRINGS — the
// component registry's example_props advertise them that way. React escapes
// string children, so without hydration the page shows literal "<div>…" text.
// Compile markup-looking strings into elements with the same sanitizing
// renderer the blog path uses (raw HTML parsed, <script> stripped). Excluded
// keys: markdown* props are compiled by the components themselves and MUST
// stay strings; *html* props (e.g. contentHtml) are rendered via
// dangerouslySetInnerHTML and must also pass through untouched.
function shouldHydrateMarkupString(key: string, value: unknown): boolean {
  return (
    typeof value === "string" &&
    /^\s*<[a-zA-Z]/.test(value) &&
    !/markdown|html/i.test(key)
  );
}

export function initOpenSiteRenderers(): void {
  const allBlocks = getAllBlocks();

  allBlocks.forEach(({ id, component: Component }: { id: string; component: React.ComponentType<any> }) => {
    registerBlockRenderer(id, ({ block }: BlockRendererProps) => {
      const rawProps = (block.blockProps ?? {}) as Record<string, unknown>;
      const props = deserializeJsx(rawProps) as Record<string, unknown>;
      return React.createElement(
        Component as React.ComponentType<Record<string, unknown>>,
        props
      );
    });
  });
}
